const STORAGE_KEY = 'safeguardpro_assignments_v1';

const readAssignments = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeAssignments = (assignments) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
};

const normalizeTimeline = (timeline = []) =>
  timeline.map((entry) => ({
    id: entry.id || `timeline-${Math.random().toString(36).slice(2, 8)}`,
    type: entry.type || 'note',
    message: entry.message || '',
    createdAt: entry.createdAt || new Date().toISOString(),
    createdBy: entry.createdBy || 'System',
    attachments: entry.attachments || [],
    studentId: entry.studentId || null,
    reviewStatus: entry.reviewStatus || entry.review_status || '',
    reviewDueDate: entry.reviewDueDate || entry.review_due_date || ''
  }));

const normalizeAssignment = (assignment) => ({
  id: assignment.id || `assign-${Math.random().toString(36).slice(2, 9)}`,
  title: assignment.title || 'Assignment',
  description: assignment.description || '',
  dueDate: assignment.dueDate || '',
  createdAt: assignment.createdAt || new Date().toISOString(),
  createdBy: assignment.createdBy || 'System',
  createdById: assignment.createdById || null,
  status: assignment.status || 'active',
  studentIds: assignment.studentIds || [],
  attachments: assignment.attachments || [],
  timeline: normalizeTimeline(assignment.timeline || [])
});

export default class AssignmentService {
  list() {
    return readAssignments().map((assignment) => normalizeAssignment(assignment));
  }

  listForStudents(studentIds = []) {
    const idSet = new Set(studentIds);
    return this.list().filter((assignment) => assignment.studentIds.some((id) => idSet.has(id)));
  }

  getById(id) {
    return this.list().find((assignment) => assignment.id === id) || null;
  }

  create({ title, description, dueDate, createdBy, createdById, studentIds, attachments = [] }) {
    const assignment = normalizeAssignment({
      title,
      description,
      dueDate,
      createdBy,
      createdById,
      studentIds,
      attachments,
      timeline: [
        {
          type: 'created',
          message: 'Assignment created.',
          createdBy
        }
      ]
    });
    const next = [assignment, ...readAssignments()];
    writeAssignments(next);
    return assignment;
  }

  addTimelineEntry(id, entry) {
    const assignments = this.list();
    const next = assignments.map((assignment) => {
      if (assignment.id !== id) return assignment;
      return {
        ...assignment,
        timeline: [
          ...assignment.timeline,
          {
            id: `timeline-${Math.random().toString(36).slice(2, 8)}`,
            type: entry.type || 'note',
            message: entry.message || '',
            createdAt: entry.createdAt || new Date().toISOString(),
            createdBy: entry.createdBy || 'System',
            attachments: entry.attachments || [],
            studentId: entry.studentId || null,
            reviewStatus: entry.reviewStatus || '',
            reviewDueDate: entry.reviewDueDate || ''
          }
        ]
      };
    });
    writeAssignments(next);
    return next.find((assignment) => assignment.id === id) || null;
  }

  update(id, updates) {
    const assignments = this.list();
    const next = assignments.map((assignment) => {
      if (assignment.id !== id) return assignment;
      return normalizeAssignment({ ...assignment, ...updates });
    });
    writeAssignments(next);
    return next.find((assignment) => assignment.id === id) || null;
  }
}
